import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import BusinessIcon from '@mui/icons-material/Business';
import { formatCurrency } from '../../../utils/currencyUtil';
import {
  useGetAllContractItemsQuery,
  useGetProcurementContractsQuery,
} from '../../../redux/procurementContractsApi';

interface ContractItemsPreviewPanelProps {
  quotations?: any[];
}

const APP_GRADIENT = 'linear-gradient(135deg, #1976d2 30%, #F7C914 90%)';

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeExact = (value: unknown) => String(value ?? '').trim().toLowerCase();

const getThresholdStyle = (current: number, initial: number): { bgcolor: string; color: string; label: string } => {
  if (!initial || initial === 0) return { bgcolor: 'transparent', color: 'inherit', label: '' };
  const pct = (current / initial) * 100;
  if (pct < 20) return { bgcolor: '#ffebee', color: '#c62828', label: `${pct.toFixed(0)}%` };
  if (pct < 50) return { bgcolor: '#fff3e0', color: '#e65100', label: `${pct.toFixed(0)}%` };
  if (pct < 80) return { bgcolor: '#e3f2fd', color: '#1565c0', label: `${pct.toFixed(0)}%` };
  return { bgcolor: '#e8f5e9', color: '#2e7d32', label: `${pct.toFixed(0)}%` };
};

const ContractItemsPreviewPanel: React.FC<ContractItemsPreviewPanelProps> = ({ quotations }) => {
  const { data: procurementContracts = [], isLoading: contractsLoading, isError: contractsError } = useGetProcurementContractsQuery();
  const { data: allContractItems = [], isLoading: itemsLoading, isError: itemsError } = useGetAllContractItemsQuery();

  const supplierGroups = React.useMemo(() => {
    const quotationList = Array.isArray(quotations) ? quotations : [];
    if (quotationList.length === 0 || allContractItems.length === 0) return [];

    return quotationList
      .map((quotation: any) => {
        const supplierId = quotation?.supplier?.id;
        const supplierName = quotation?.supplier?.name || 'Unknown Supplier';
        if (!supplierId) return null;

        const activeSupplierContractIds = new Set(
          procurementContracts
            .filter((contract) => contract.active && String(contract.supplier?.id) === String(supplierId))
            .map((contract) => String(contract.id)),
        );

        if (activeSupplierContractIds.size === 0) return null;

        const contractItemsByKey = new Map<string, any>();
        allContractItems.forEach((contractItem: any) => {
          if (!activeSupplierContractIds.has(String(contractItem.contractId))) return;
          const key = `${normalizeExact(contractItem.itemCode)}|${normalizeExact(contractItem.itemDescription)}`;
          if (!contractItemsByKey.has(key)) {
            contractItemsByKey.set(key, contractItem);
          }
        });

        if (contractItemsByKey.size === 0) return null;

        const quotationItems = Array.isArray(quotation?.quotationItems) ? quotation.quotationItems : [];
        const matchedRows = quotationItems
          .map((quotationItem: any) => {
            const key = `${normalizeExact(quotationItem?.itemCode)}|${normalizeExact(quotationItem?.description)}`;
            const matchedContractItem = contractItemsByKey.get(key);
            if (!matchedContractItem) return null;

            const contractRate = toNumber(matchedContractItem.itemBudgetedCost);
            const quotedRate = toNumber(quotationItem?.rate);

            return {
              id: String(matchedContractItem.id),
              itemCode: matchedContractItem.itemCode || quotationItem.itemCode,
              itemDescription: matchedContractItem.itemDescription || quotationItem.description,
              itemUnit: matchedContractItem.itemUnit || quotationItem?.quantity?.quantityUnit || '',
              isVAT: !!matchedContractItem.isVAT,
              contractRate,
              quotedRate,
              rateVariance: Math.round((quotedRate - contractRate) * 100) / 100,
              initialQuantity: toNumber(matchedContractItem.initialQuantity),
              itemQuantity: toNumber(matchedContractItem.itemQuantity),
              itemInitialValue: toNumber(matchedContractItem.itemInitialValue),
              itemCurrentValue: toNumber(matchedContractItem.itemCurrentValue),
            };
          })
          .filter(Boolean);

        if (matchedRows.length === 0) return null;

        return {
          supplierId: String(supplierId),
          supplierName,
          rows: matchedRows,
        };
      })
      .filter(Boolean) as Array<{ supplierId: string; supplierName: string; rows: any[] }>;
  }, [quotations, procurementContracts, allContractItems]);

  if (!Array.isArray(quotations) || quotations.length === 0) return null;
  if (contractsLoading || itemsLoading) {
    return (
      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center', mt: 3 }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }
  if (contractsError || itemsError || supplierGroups.length === 0) return null;

  return (
    <Card
      sx={{
        mt: 3,
        borderRadius: 3,
        border: '1px solid #bbdefb',
        background: 'linear-gradient(180deg, #e3f2fd 0%, #ffffff 70%)',
        boxShadow: '0 8px 20px rgba(25, 118, 210, 0.18)',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <HandshakeIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0d47a1' }}>
            Contract Items Preview
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#1565c0', mb: 2 }}>
          Showing active contract items for this supplier
        </Typography>

        {supplierGroups.map((group, groupIndex) => (
          <Box key={group.supplierId} sx={{ mb: groupIndex < supplierGroups.length - 1 ? 3 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BusinessIcon fontSize="small" sx={{ color: '#1976d2' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0d47a1' }}>
                {group.supplierName}
              </Typography>
              <Chip size="small" label={`${group.rows.length} matched item(s)`} color="primary" variant="outlined" />
            </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2, border: '1px solid #bbdefb' }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#1976d2' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Item Code</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Unit</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Contract Rate</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Quoted Rate</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Variance</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">VAT</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Initial Qty</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Remaining Qty</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Initial Value</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Remaining Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.rows.map((row) => (
                    <TableRow key={`${group.supplierId}-${row.id}-${row.itemCode}`} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                      <TableCell>{row.itemCode}</TableCell>
                      <TableCell>{row.itemDescription}</TableCell>
                      <TableCell>{row.itemUnit}</TableCell>
                      <TableCell align="right">{formatCurrency(row.contractRate)}</TableCell>
                      <TableCell align="right">{formatCurrency(row.quotedRate)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color: row.rateVariance > 0 ? '#c62828' : row.rateVariance < 0 ? '#2e7d32' : 'inherit',
                        }}
                      >
                        {row.rateVariance > 0 ? '+' : ''}{formatCurrency(row.rateVariance)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={row.isVAT ? 'VAT' : 'No VAT'}
                          color={row.isVAT ? 'warning' : 'default'}
                          variant={row.isVAT ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell align="right">{row.initialQuantity}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: getThresholdStyle(row.itemQuantity, row.initialQuantity).bgcolor }}>
                        {row.itemQuantity}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(row.itemInitialValue)}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: getThresholdStyle(row.itemCurrentValue, row.itemInitialValue).bgcolor }}>
                        {formatCurrency(row.itemCurrentValue)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {groupIndex < supplierGroups.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

export default ContractItemsPreviewPanel;
